import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import * as React from 'react';
import { c } from '../../../utils/tailwind-merge';
import { usePopoverContext } from './Popover.context';

export const PopoverContent = React.forwardRef<
  HTMLDivElement,
  { className?: string; children?: React.ReactNode }
>(({ className = '', children }, forwardedRef) => {
  const {
    getFloatingProps,
    refs,
    floatingStyles,
    context,
    open,
    id,
    roleProp,
    modal,
    initialFocus,
    arrowRef,
    middlewareData,
    placement,
  } = usePopoverContext();

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement?.split('-')[0] ?? 'top'];

  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      refs.setFloating(node as HTMLElement | null);
      if (!forwardedRef) return;
      if (typeof forwardedRef === 'function')
        forwardedRef(node as HTMLDivElement | null);
      else
        (
          forwardedRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node as HTMLDivElement | null;
    },
    [refs, forwardedRef]
  );

  const arrowStyle: React.CSSProperties | undefined = React.useMemo(() => {
    if (!middlewareData || !middlewareData.arrow) return undefined;
    const base: React.CSSProperties = {
      position: 'absolute',
      left: middlewareData.arrow.x ?? 0,
      top: middlewareData.arrow.y ?? 0,
    };
    if (staticSide) {
      (base as any)[staticSide] = '-8px';
    }
    return base;
  }, [middlewareData, staticSide]);

  const arrowClass = React.useMemo(() => {
    switch (staticSide) {
      case 'top':
        return 'h-0 w-0 border-b-[8px] border-l-[8px] border-r-[8px] border-b-surface-raised border-l-transparent border-r-transparent';
      case 'bottom':
        return 'h-0 w-0 border-t-[8px] border-l-[8px] border-r-[8px] border-t-surface-raised border-l-transparent border-r-transparent';
      case 'left':
        return 'h-0 w-0 border-r-[8px] border-t-[8px] border-b-[8px] border-r-surface-raised border-t-transparent border-b-transparent';
      case 'right':
        return 'h-0 w-0 border-l-[8px] border-t-[8px] border-b-[8px] border-l-surface-raised border-t-transparent border-b-transparent';
      default:
        return 'h-0 w-0 border-b-[8px] border-l-[8px] border-r-[8px] border-b-surface-raised border-l-transparent border-r-transparent';
    }
  }, [staticSide]);

  const arrowBorderClass = React.useMemo(() => {
    switch (staticSide) {
      case 'top':
        return 'h-0 w-0 border-b-[9px] border-l-[9px] border-r-[9px] border-b-line border-l-transparent border-r-transparent';
      case 'bottom':
        return 'h-0 w-0 border-t-[9px] border-l-[9px] border-r-[9px] border-t-line border-l-transparent border-r-transparent';
      case 'left':
        return 'h-0 w-0 border-r-[9px] border-t-[9px] border-b-[9px] border-r-line border-t-transparent border-b-transparent';
      case 'right':
        return 'h-0 w-0 border-l-[9px] border-t-[9px] border-b-[9px] border-l-line border-t-transparent border-b-transparent';
      default:
        return 'h-0 w-0 border-b-[9px] border-l-[9px] border-r-[9px] border-b-line border-l-transparent border-r-transparent';
    }
  }, [staticSide]);

  const arrowBorderStyle: React.CSSProperties | undefined =
    React.useMemo(() => {
      if (!arrowStyle) return undefined;
      const copy = { ...arrowStyle } as React.CSSProperties;
      if (staticSide) {
        (copy as any)[staticSide] = '-9px';
      }
      return copy;
    }, [arrowStyle, staticSide]);

  return (
    <>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={modal}
            initialFocus={initialFocus as any}
          >
            <div
              id={id}
              ref={setRef}
              style={floatingStyles}
              className={c(
                'relative max-w-xs rounded border border-line bg-surface-raised px-3 py-4 shadow-card',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-canvas dark:focus:ring-focus',
                className
              )}
              {...getFloatingProps()}
              role={roleProp}
              aria-modal={modal}
            >
              {arrowBorderStyle && arrowRef && (
                <div
                  ref={arrowRef as any}
                  style={arrowBorderStyle}
                  className={arrowBorderClass}
                />
              )}
              {arrowStyle && arrowRef && (
                <div
                  ref={arrowRef as any}
                  style={arrowStyle}
                  className={arrowClass}
                />
              )}
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
});
PopoverContent.displayName = 'PopoverContent';
